/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {MessageRecord} from '@app/records/MessageRecord';
import ChannelStore from '@app/stores/ChannelStore';
import GuildStore from '@app/stores/GuildStore';
import PermissionStore from '@app/stores/PermissionStore';
import SelectedChannelStore from '@app/stores/SelectedChannelStore';
import * as ChannelUtils from '@app/utils/ChannelUtils';
import {ChannelTypes, Permissions} from '@fluxer/constants/src/ChannelConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {useCallback, useMemo, useState} from 'react';

const FORWARDABLE_CHANNEL_TYPES = new Set<number>(
	Object.values(ChannelTypes)
		.filter((value) => typeof value === 'number' && value !== ChannelTypes.GUILD_CATEGORY && value !== ChannelTypes.GUILD_VOICE)
		.map((value) => Number(value)),
);

export const getForwardChannelDisplayName = (channel: ChannelRecord, i18n?: I18n): string => {
	if (
		channel.type === ChannelTypes.DM_PERSONAL_NOTES ||
		channel.type === ChannelTypes.DM ||
		channel.type === ChannelTypes.GROUP_DM
	) {
		return ChannelUtils.getDMDisplayName(channel);
	}

	if (channel.name) return channel.name;

	if (!i18n) return `Channel ${channel.id}`;

	const id = channel.id;
	return i18n._(msg`Channel ${id}`);
};

export const getForwardChannelGuildName = (channel: ChannelRecord): string | null => {
	if (!channel.guildId) return null;
	const guild = GuildStore.getGuild(channel.guildId);
	return guild?.name ?? null;
};

export const getForwardChannelCategoryName = (channel: ChannelRecord): string | null => {
	if (!channel.parentId) return null;
	const category = ChannelStore.getChannel(channel.parentId);
	if (!category) return null;
	return category.name || null;
};

function messageHasMedia(message: MessageRecord): {hasEmbeds: boolean; hasAttachments: boolean} {
	const hasEmbeds =
		message.embeds.length > 0 ||
		(message.messageSnapshots?.some((s) => s.embeds != null && s.embeds.length > 0) ?? false);
	const hasAttachments =
		message.attachments.length > 0 ||
		(message.messageSnapshots?.some((s) => s.attachments != null && s.attachments.length > 0) ?? false);
	return {hasEmbeds, hasAttachments};
}

interface UseForwardChannelSelectionOptions {
	excludedChannelId: string;
	message: MessageRecord;
	maxSelections?: number;
}

export const useForwardChannelSelection = ({
	excludedChannelId,
	message,
	maxSelections = 5,
}: UseForwardChannelSelectionOptions) => {
	const {i18n} = useLingui();
	const recentChannelIds = SelectedChannelStore.recentChannels;
	const {hasEmbeds, hasAttachments} = useMemo(() => messageHasMedia(message), [message]);

	const [searchQuery, setSearchQuery] = useState('');
	const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());

	const allChannels = useMemo(() => {
		const channels = ChannelStore.allChannels.filter((channel) => FORWARDABLE_CHANNEL_TYPES.has(channel.type));

		return channels.sort((a, b) => {
			const aIsSource = a.id === excludedChannelId;
			const bIsSource = b.id === excludedChannelId;

			if (aIsSource && bIsSource) return 0;
			if (aIsSource) return 1;
			if (bIsSource) return -1;

			const aIndex = recentChannelIds.indexOf(a.id);
			const bIndex = recentChannelIds.indexOf(b.id);

			if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
			if (aIndex !== -1) return -1;
			if (bIndex !== -1) return 1;

			const aName = getForwardChannelDisplayName(a, i18n).toLowerCase();
			const bName = getForwardChannelDisplayName(b, i18n).toLowerCase();
			return aName.localeCompare(bName);
		});
	}, [recentChannelIds, excludedChannelId, i18n]);

	const filteredChannels = useMemo(() => {
		if (!searchQuery.trim()) return allChannels;

		const query = searchQuery.toLowerCase();
		return allChannels.filter((channel) => {
			const channelName = (channel.name || '').toLowerCase();
			const displayName = getForwardChannelDisplayName(channel, i18n).toLowerCase();
			const guildName = channel.guildId ? (GuildStore.getGuild(channel.guildId)?.name ?? '').toLowerCase() : '';

			if (channel.type === ChannelTypes.DM_PERSONAL_NOTES) {
				const notes = i18n._(msg`Personal Notes`).toLowerCase();
				if (notes.includes(query)) return true;
			}

			return displayName.includes(query) || channelName.includes(query) || guildName.includes(query);
		});
	}, [allChannels, searchQuery, i18n]);

	const handleToggleChannel = useCallback(
		(channelId: string) => {
			setSelectedChannelIds((prev) => {
				const next = new Set(prev);

				if (next.has(channelId)) {
					next.delete(channelId);
					return next;
				}

				if (next.size >= maxSelections) return prev;

				next.add(channelId);
				return next;
			});
		},
		[maxSelections],
	);

	const getChannelMediaPermissionIssue = useCallback(
		(channel: ChannelRecord): string | null => {
			if (!channel.guildId) return null;
			if (!hasEmbeds && !hasAttachments) return null;
			if (
				hasEmbeds &&
				!PermissionStore.can(Permissions.EMBED_LINKS, {channelId: channel.id, guildId: channel.guildId})
			) {
				return i18n._(msg`You don't have permission to embed links in this channel`);
			}
			if (
				hasAttachments &&
				!PermissionStore.can(Permissions.ATTACH_FILES, {channelId: channel.id, guildId: channel.guildId})
			) {
				return i18n._(msg`You don't have permission to attach files in this channel`);
			}
			return null;
		},
		[hasEmbeds, hasAttachments, i18n],
	);

	const isChannelDisabled = useCallback(
		(channelId: string) => {
			if (!selectedChannelIds.has(channelId) && selectedChannelIds.size >= maxSelections) return true;
			const channel = ChannelStore.getChannel(channelId);
			if (channel && getChannelMediaPermissionIssue(channel) != null) return true;
			return false;
		},
		[selectedChannelIds, maxSelections, getChannelMediaPermissionIssue],
	);

	const getChannelDisableReason = useCallback(
		(channel: ChannelRecord): string | null => {
			return getChannelMediaPermissionIssue(channel);
		},
		[getChannelMediaPermissionIssue],
	);

	return {
		filteredChannels,
		handleToggleChannel,
		isChannelDisabled,
		getChannelDisableReason,
		searchQuery,
		selectedChannelIds,
		setSearchQuery,
		maxSelections,
	};
};
